import classNames from "classnames";
import { PropsWithChildren, useRef } from "react";
import styles from "./Dialog.module.css";
import Icon from "./Icon";
import Close from "./icons/close.svg?react";

type DialogSize = "default" | "small" | "fullscreen" | "bottom";

type Props = PropsWithChildren<{
  open: boolean;
  size?: DialogSize;
  isDismissable?: boolean;
  onClose: () => void;
}>;

const Dialog = ({
  open: isOpen,
  size = "default",
  isDismissable = false,
  children,
  onClose,
}: Props) => {
  const dialogRef = useRef<HTMLDialogElement>(null);

  const open = () => !dialogRef.current?.open && dialogRef.current?.showModal();
  const close = () => dialogRef.current?.open && dialogRef.current.close();

  isOpen ? open() : close();

  return (
    <dialog
      ref={dialogRef}
      className={classNames(styles.dialog, styles.default, {
        [styles.small]: size === "small",
        [styles.fullScreen]: size === "fullscreen",
        [styles.bottom]: size === "bottom",
      })}
      onClose={onClose}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
    >
      {isOpen && (
        <div className={styles.wrapper}>
          {isDismissable && (
            <button
              type="button"
              className={styles.closeButton}
              onClick={onClose}
            >
              <Icon className={styles.closeIcon} glyph={Close} />
            </button>
          )}
          {children}
        </div>
      )}
    </dialog>
  );
};

const Title = ({ children }: PropsWithChildren) => {
  return <h3 className={styles.title}>{children}</h3>;
};

Dialog.Title = Title;

const Content = ({ children }: PropsWithChildren) => {
  return <div className={classNames(styles.content)}>{children}</div>;
};

Dialog.Content = Content;

const Footer = ({ children }: PropsWithChildren) => {
  return <div className={styles.footer}>{children}</div>;
};

Dialog.Footer = Footer;

export default Dialog;
